@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import ai.wagemix.net.client.WagemixClientAPI
import ai.wagemix.net.models.WMBillingPortalSessionCreate
import ai.wagemix.net.models.WMCheckoutSessionCreate
import ai.wagemix.net.models.kxs
import com.fsryan.foundation.data.FSSyncDataSource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Billing through the Wagemix API, which owns the Stripe integration. Prices, the checkout
 * summary and the redirect URLs land in the global local context; the presenter follows a
 * redirect URL once and deletes it.
 */
interface WMBillingSyncDataSource: FSSyncDataSource<WMBillingSyncEvent<*>> {
    fun fetchPrices(additionalContext: Map<String, Any> = emptyMap())
    fun startCheckout(lookupKey: String, additionalContext: Map<String, Any> = emptyMap())
    fun openBillingPortal(returnPath: String?, additionalContext: Map<String, Any> = emptyMap())
    fun fetchCheckoutSummary(sessionId: String, additionalContext: Map<String, Any> = emptyMap())
}

fun WMBillingSyncDataSource(api: WagemixClientAPI, localContext: WMLocalContextDataSource): WMBillingSyncDataSource {
    return WMBillingSyncDataSourceImpl(api = api, localContext = localContext)
}

internal class WMBillingSyncDataSourceImpl(
    private val api: WagemixClientAPI,
    localContext: WMLocalContextDataSource,
    scope: CoroutineScope = CoroutineScope(SupervisorJob())
): WMBaseSyncDataSource<WMBillingSyncEvent<*>>(localContext, scope), WMBillingSyncDataSource {

    override fun fetchPrices(additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_PRICES,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMBillingSyncEvent.FetchPrices.Initial(sequenceId, additionalContext) },
                success = { WMBillingSyncEvent.FetchPrices.Success(sequenceId, additionalContext) },
                failure = { error -> WMBillingSyncEvent.FetchPrices.Failure(error, sequenceId, additionalContext) },
                remote = { api.fetchPlanPrices().dataOrThrow { it?.message ?: "No prices in the response" } },
                local = { prices -> localContext.upsertPlanPrices(prices) }
            )
        }
    }

    override fun startCheckout(lookupKey: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_CHECKOUT,
                scope = lookupKey,
                sequenceId = sequenceId,
                initial = { WMBillingSyncEvent.StartCheckout.Initial(lookupKey, sequenceId, additionalContext) },
                success = { WMBillingSyncEvent.StartCheckout.Success(lookupKey, sequenceId, additionalContext) },
                failure = { error -> WMBillingSyncEvent.StartCheckout.Failure(lookupKey, error, sequenceId, additionalContext) },
                remote = {
                    api.createCheckoutSession { body(WMCheckoutSessionCreate.kxs(lookupKey = lookupKey)) }
                        .dataOrThrow { it?.message ?: "No checkout URL in the response" }
                },
                local = { redirect -> localContext.upsertCheckoutUrl(lookupKey, redirect.url) }
            )
        }
    }

    override fun openBillingPortal(returnPath: String?, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_PORTAL,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMBillingSyncEvent.OpenBillingPortal.Initial(returnPath, sequenceId, additionalContext) },
                success = { WMBillingSyncEvent.OpenBillingPortal.Success(returnPath, sequenceId, additionalContext) },
                failure = { error -> WMBillingSyncEvent.OpenBillingPortal.Failure(returnPath, error, sequenceId, additionalContext) },
                remote = {
                    api.createBillingPortalSession { body(WMBillingPortalSessionCreate.kxs(returnPath = returnPath)) }
                        .dataOrThrow { it?.message ?: "No billing portal URL in the response" }
                },
                local = { redirect -> localContext.upsertBillingPortalUrl(redirect.url) }
            )
        }
    }

    override fun fetchCheckoutSummary(sessionId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SUMMARY,
                scope = sessionId,
                sequenceId = sequenceId,
                initial = { WMBillingSyncEvent.FetchCheckoutSummary.Initial(sessionId, sequenceId, additionalContext) },
                success = { WMBillingSyncEvent.FetchCheckoutSummary.Success(sessionId, sequenceId, additionalContext) },
                failure = { error -> WMBillingSyncEvent.FetchCheckoutSummary.Failure(sessionId, error, sequenceId, additionalContext) },
                remote = {
                    api.fetchCheckoutSession { sessionId(sessionId) }.dataOrThrow { it?.message ?: "No checkout summary in the response" }
                },
                local = { summary -> localContext.upsertCheckoutSummary(sessionId, summary) }
            )
        }
    }

    private companion object {
        const val OP_PRICES = "billing.prices"
        const val OP_CHECKOUT = "billing.checkout"
        const val OP_PORTAL = "billing.portal"
        const val OP_SUMMARY = "billing.checkoutSummary"
    }
}
